import { useEffect, useRef, useState } from 'react';
import { useOutletContext, useNavigate, useParams } from 'react-router-dom';
import type { InboxDuplicateRef, InboxVerifyDetail, SettingsPco } from '@coffman-tm/shared';
import { AlertTriangle, ArrowLeft, CheckCircle2, FileText, Inbox } from 'lucide-react';
import { useInboxPdfDetail } from '../../lib/useInbox';
import { useApiQuery } from '../../lib/useApiQuery';
import { api, ApiCallError } from '../../lib/api';
import { useAuth } from '../../lib/AuthContext';
import { useToast } from '../ToastProvider';
import { formatDateShort } from '../../lib/format';
import { InboxBlockCard } from './InboxBlockCard';
import { PdfCompareModal } from '../PdfCompareModal';
import { HelpTip } from '../HelpTip';
import { EmptyState, Skeleton } from '../design';

/**
 * Context the inbox shell (DashboardPage) hands down to the detail route via
 * <Outlet context>. Keeps the PCO list, the master-list refetch, and the PDF
 * preview modal trigger owned by the parent so the detail stays a thin leaf.
 */
export interface InboxOutletContext {
  pcos: SettingsPco[];
  refetchInbox: () => Promise<void>;
  openPdfPreview: (pdfId: string) => void;
  /** Count of PDFs still awaiting review, from the master list — lets the
   *  empty detail state read as "all caught up" instead of a generic prompt.
   *  null while the list is still loading: the all-clear is the one claim
   *  this pane must never make before the data exists. */
  pdfsPending: number | null;
}

function useInboxOutlet() {
  return useOutletContext<InboxOutletContext>();
}

/** Index route — shown in the detail pane when no PDF is selected. */
export function InboxDetailEmpty() {
  const { pdfsPending } = useInboxOutlet();
  // Still loading: a quiet blank pane. Rendering either message here would be
  // a guess, and the wrong guess ("all caught up") flashes a false all-clear.
  if (pdfsPending === null) return <div className="inbox-detail-empty" />;
  return (
    <div className="inbox-detail-empty">
      {pdfsPending > 0 ? (
        <EmptyState flush icon={Inbox} text="Select a PDF from the list to review." />
      ) : (
        <EmptyState
          flush
          icon={CheckCircle2}
          tone="positive"
          text="You're all caught up — no PDFs waiting for review."
        />
      )}
    </div>
  );
}

/**
 * The ISO stamp's date IN THE READER'S ZONE. `at.slice(0, 10)` would print the
 * UTC day, so a 6pm-Pacific click reads as tomorrow.
 */
function localDayOf(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * The two ways the dismiss can be refused, in PM English. Both mean "the
 * server's state moved on", which is never the PM's fault and never worth
 * showing him a machine code for.
 */
function ackErrorMessage(e: unknown): string {
  if (e instanceof ApiCallError) {
    const code = (e.payload as { error?: string })?.error;
    if (code === 'not_flagged') return 'This report is not flagged any more — the note was already cleared.';
    if (code === 'verification_changed')
      return 'This flag was already cleared — refresh to see the latest.';
    if (code === 'not_found') return 'This report is no longer available on this project. Refresh to see the latest.';
    if (e.status === 409) return 'This flag was already cleared — refresh to see the latest.';
  }
  return e instanceof Error ? e.message : 'Please try again.';
}

/**
 * The daily-level Tier-2 line, under the header. Names which blocks the second
 * read disagreed about — the block cards carry the row detail — and offers the
 * ONE action that clears the flag.
 *
 * The dismiss lives here, not on the card, because the flag is a fact about the
 * DAILY: the composer asks about the report, the list tags the report, and a
 * per-block dismiss would leave a PM clicking three buttons to silence one tag.
 *
 * Advisory throughout. Nothing here disables Save, and nothing clears itself —
 * a save is a billing decision, not a verification.
 */
function VerifyFlagStrip({
  verify,
  dailyPdfId,
  onViewPdf,
  onAcknowledged,
}: {
  verify: InboxVerifyDetail;
  dailyPdfId: string;
  onViewPdf: () => void;
  onAcknowledged: () => Promise<void>;
}) {
  const { token } = useAuth();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  // Already settled by a human — a quiet receipt, not a warning. Without it the
  // strip simply vanishes on click, which reads as "did that do anything?".
  if (verify.status !== 'flagged') {
    if (!verify.acknowledged) return null;
    return (
      <div className="inbox-verify-strip acked">
        <CheckCircle2 size={14} aria-hidden style={{ flexShrink: 0, marginTop: 2 }} />
        <span>
          {'Second read checked against the PDF on '}
          {/* The stamp is UTC; slicing its date off would date a 6pm Pacific
              click to the following day. Render it in the reader's zone. */}
          {formatDateShort(localDayOf(verify.acknowledged.at))}
          {' — the recorded rows were confirmed right.'}
        </span>
      </div>
    );
  }

  const acknowledge = async () => {
    if (!token || busy) return;
    setBusy(true);
    try {
      await api.inbox.acknowledgeVerify(token, dailyPdfId);
      toast.push({
        variant: 'success',
        title: 'Marked as checked',
        body: 'The second-read note is cleared for this report.',
      });
      await onAcknowledged();
    } catch (e) {
      toast.push({
        variant: 'error',
        title: 'Could not clear the note',
        body: ackErrorMessage(e),
      });
      // Both 409s mean the server's state has moved on. Re-read it rather than
      // leaving the PM looking at a strip that no longer describes anything.
      if (e instanceof ApiCallError && e.status === 409) await onAcknowledged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="inbox-verify-strip">
      <AlertTriangle size={14} aria-hidden style={{ flexShrink: 0, marginTop: 2 }} />
      <div className="inbox-verify-strip-body">
        <span>
          {verify.headline}
          <HelpTip title="What the second read is telling you" learnMore="troubleshooting">
            A second AI model reads each foreman&rsquo;s report on its own and types out what is
            printed. This note means that transcription did not match what was recorded. Open the
            PDF and compare the rows named on each block. If what was recorded is right, mark it
            checked — the re-reading model is not infallible either. If the PDF is right, fix the
            block the normal way instead.
          </HelpTip>
        </span>
        <div className="inbox-verify-strip-actions">
          {/* View PDF is the resolution tool — the question is only answerable
              against the page, so it sits beside the claim rather than only in
              the header. */}
          <button type="button" className="inbox-verify-btn ghost" onClick={onViewPdf}>
            <FileText size={13} aria-hidden />
            View PDF
          </button>
          {/* `can_acknowledge` is the SERVER's answer to "is the dismiss
              available", not a restatement of the status. Reading the status
              instead would keep offering the button the day the server narrows
              the rule (a locked week, a role that may not clear flags), and the
              PM would meet the refusal as an error toast. */}
          {verify.can_acknowledge && (
            <button
              type="button"
              className="inbox-verify-btn"
              onClick={acknowledge}
              disabled={busy || !token}
            >
              {busy ? 'Saving…' : 'Checked against the PDF — parse is right'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Detail pane — the selected PDF's metadata header + its blocks, each expanded
 * by default. Renders inside the inbox shell's <Outlet>; the selected id comes
 * from the route param, not local state.
 */
export function InboxPdfDetail() {
  const { dailyPdfId } = useParams<{ dailyPdfId: string }>();
  const navigate = useNavigate();
  const { pcos, refetchInbox, openPdfPreview } = useInboxOutlet();
  const { data, isLoading, error, refetch } = useInboxPdfDetail(dailyPdfId ?? null);
  // Project roster defaults — prefill the inbox quick-add (Add to roster) modal.
  const { data: project } = useApiQuery((t, signal) => api.settings.project(t, { signal }));

  const refetchAll = async () => {
    await Promise.all([refetch(), refetchInbox()]);
  };

  // Finding #4: once every block on this PDF is saved or skipped, the master
  // list already drops the row ("No PDFs waiting for review") — but without
  // this, the detail pane kept showing the now-finished blocks as if there
  // were still something to do here. Flash a quick "fully processed"
  // confirmation, then fall back to the empty state (same route the Back
  // button uses) so the pane matches the master list.
  const [showComplete, setShowComplete] = useState(false);
  /** The flagged sibling to compare this PDF against; null = compare closed. */
  const [compareWith, setCompareWith] = useState<InboxDuplicateRef | null>(null);
  /**
   * Did this PDF arrive at "fully decided" UNDER THE PM'S HANDS, or was it
   * already finished when he opened it? Since 2026-08-06 the list reveals
   * decided PDFs whose skipped blocks have nowhere else to live ("N skipped
   * reports — show"), so opening a finished report is a thing he does on
   * purpose — and the flash below would replace the very cards carrying
   * Re-open, then navigate him away 1.1s later.
   *
   * So the flag is not "was it decided at first sight" but a LATCH THAT
   * RE-ARMS: any render where work is waiting on this PDF sets `decided:false`,
   * which is the first load of an ordinary report AND the moment a Re-open in
   * this very pane puts a block back. Whatever finishes after that finishes
   * under his hands and gets the flash + bounce, exactly like the
   * last-block-decided flow it exists for. Only a PDF whose FIRST sight was
   * already decided latches `true`, and it stays that way until something on
   * it becomes pending again.
   *
   * Keyed by id because the pane is one component across `/inbox/:id` changes:
   * navigating A → B → A re-judges A on the state it has when he comes back,
   * and leaving the pane entirely unmounts it, which drops the latch — both
   * correct, since the question is always about the open he just performed.
   */
  const arrivedDecidedRef = useRef<{ id: string; decided: boolean } | null>(null);

  useEffect(() => {
    setShowComplete(false);
  }, [dailyPdfId]);

  useEffect(() => {
    if (!data || data.blocks.length === 0) return;
    if (data.daily_pdf_id !== dailyPdfId) return;
    const stillPending = data.blocks.some((b) => b.block_status === 'pending');
    if (stillPending) {
      // RE-ARM. Work is waiting again — either the ordinary first load, or a
      // Re-open the PM just clicked on a revealed report. Either way the next
      // finish is his, so it must flash and bounce like any other.
      arrivedDecidedRef.current = { id: data.daily_pdf_id, decided: false };
      return;
    }
    if (arrivedDecidedRef.current?.id !== data.daily_pdf_id) {
      // First sight of this PDF and it is ALREADY decided: he opened a
      // finished report on purpose. Keep the cards — and the Re-open on them.
      arrivedDecidedRef.current = { id: data.daily_pdf_id, decided: true };
    }
    if (arrivedDecidedRef.current.decided) return;
    setShowComplete(true);
    // An UNRESOLVED re-read flag holds the pane open. Tier 2 drains a few
    // dailies an hour, so it routinely reaches a report days after the PM
    // finished reviewing it — and bouncing him out 1.1s after he opened the
    // only surface that explains the flag (and carries the button that clears
    // it) would put the "nowhere to look" bug back, with a timer on it.
    if (data.verify?.can_acknowledge) return;
    const t = setTimeout(() => navigate('/', { replace: true }), 1100);
    return () => clearTimeout(t);
  }, [data, navigate]);

  const subParts: string[] = [];
  if (data) {
    if (data.foreman_name) subParts.push(data.foreman_name);
    if (data.report_date) subParts.push(formatDateShort(data.report_date));
    const total = data.blocks.length;
    const processed = data.blocks.filter((b) => b.block_status !== 'pending').length;
    subParts.push(`${processed} of ${total} processed`);
    // No "N materials pending" part any more (M12, 2026-08-03). Parser material
    // candidates are stored context, not review work — counting them here told
    // the PM he had something left to do on a report he had finished.
  }

  return (
    <>
      <div className="inbox-detail-head">
        <button
          type="button"
          className="inbox-back"
          onClick={() => navigate('/')}
          aria-label="Back to list"
        >
          <ArrowLeft size={15} aria-hidden />
          Back
        </button>
        <div className="inbox-detail-head-main">
          {data ? (
            <>
              <div className="inbox-detail-title">{data.filename}</div>
              {subParts.length > 0 && <div className="inbox-detail-sub">{subParts.join(' · ')}</div>}
            </>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <Skeleton style={{ width: 220, height: 18 }} />
              <Skeleton style={{ width: 150, height: 13 }} />
            </div>
          )}
        </div>
        {data && (
          <button
            type="button"
            onClick={() => openPdfPreview(data.daily_pdf_id)}
            className="inbox-view-pdf"
            title="View original PDF"
          >
            <FileText size={14} aria-hidden />
            View PDF
          </button>
        )}
      </div>

      <div className="inbox-detail-body">
        {/* Rendered under `showComplete` too. A fully-processed daily is
            exactly when a late Tier-2 flag lands, and hiding the strip behind
            the "nothing left to review" state would hide the only place the
            flag can be read or cleared. */}
        {data && data.verify && (
          <VerifyFlagStrip
            verify={data.verify}
            dailyPdfId={data.daily_pdf_id}
            onViewPdf={() => openPdfPreview(data.daily_pdf_id)}
            onAcknowledged={refetchAll}
          />
        )}
        {isLoading && !data && (
          <div aria-hidden style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {[0, 1].map((i) => (
              <div
                key={i}
                style={{
                  border: '1px solid var(--rule)',
                  borderLeft: '3px solid var(--rule-strong)',
                  borderRadius: 4,
                  background: 'var(--surface)',
                  padding: 16,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                }}
              >
                <Skeleton style={{ width: '30%', height: 14 }} />
                <Skeleton style={{ width: '90%', height: 12 }} />
                <Skeleton style={{ width: '70%', height: 12 }} />
              </div>
            ))}
          </div>
        )}
        {error && (
          <div style={{ color: 'var(--danger)', fontSize: 13 }}>
            Failed to load blocks: {error.message}
          </div>
        )}
        {data && data.blocks.length === 0 && (
          <div className="muted" style={{ fontSize: 13 }}>
            No blocks parsed from this PDF.
          </div>
        )}
        {/* The "fully processed" flash replaces the cards — so an unresolved
            re-read flag suppresses it and keeps them. The strip above names the
            blocks; collapsing them away would leave the PM told which blocks to
            check and shown none of them. */}
        {showComplete && !data?.verify?.can_acknowledge ? (
          <div
            className="muted"
            style={{ fontSize: 13, padding: '40px 0', textAlign: 'center' }}
          >
            ✓ Fully processed — nothing left to review on this PDF.
          </div>
        ) : (
          data?.blocks.map((b) => (
            <InboxBlockCard
              key={b.daily_entry_id}
              block={b}
              pcos={pcos}
              reportDate={data.report_date}
              onComparePdfs={setCompareWith}
              onChanged={refetchAll}
              defaultStRateCents={project?.default_st_rate_cents ?? 0}
              defaultPerDiemEligible={project?.default_per_diem_eligible ?? false}
            />
          ))
        )}
      </div>

      {/* Compare lives here, not in the card: the card knows the flagged sibling
          but never the current PDF's own id or filename, and this component owns
          both. One modal for the whole detail view — every block on this PDF
          compares against the same left-hand document. */}
      {compareWith && data && (
        <PdfCompareModal
          left={{
            pdfId: data.daily_pdf_id,
            filename: data.filename,
            badge: 'this PDF',
          }}
          right={{
            pdfId: compareWith.daily_pdf_id,
            filename: compareWith.filename,
            badge: compareWith.block_status === 'saved' ? 'saved' : 'pending',
            badgeTone: 'danger',
          }}
          onClose={() => setCompareWith(null)}
        />
      )}
    </>
  );
}
